import React, { useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps, GeneralHazardItem, ECMDriver, ECM_LABELS, HierarchyControls } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
interface GeneralHazardsScreenProps extends ScreenProps {
  generalHazards: GeneralHazardItem[];
  generalEquipment: string[];
  conditions: string[];
  onUpdateGeneral: (hazards: GeneralHazardItem[], equipment: string[]) => void;
}
export function GeneralHazardsScreen({
  generalHazards,
  generalEquipment,
  conditions,
  onUpdateGeneral,
  onNext
}: GeneralHazardsScreenProps) {
  const [editingField, setEditingField] = useState<{
    hazardId: string;
    field: 'eliminateSubstitute' | 'engineering' | 'adminTraining' | 'ppe';
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showAddHazardEquipment, setShowAddHazardEquipment] = useState<string | null>(null);
  const [newHazardEquipment, setNewHazardEquipment] = useState('');
  const [showAddGeneralEquipment, setShowAddGeneralEquipment] = useState(false);
  const [newGeneralEquipment, setNewGeneralEquipment] = useState('');
  const handleUpdateControl = (id: string, field: keyof HierarchyControls, value: string) => {
    const newHazards = generalHazards.map(h => h.id === id ? {
      ...h,
      controls: {
        ...h.controls,
        [field]: value
      }
    } : h);
    onUpdateGeneral(newHazards, generalEquipment);
  };
  const handleCycleECM = (hazard: GeneralHazardItem) => {
    if (!hazard.relevant) return;
    const cycle: Record<ECMDriver, ECMDriver> = {
      E: 'C',
      C: 'M',
      M: 'E'
    };
    const newHazards = generalHazards.map(h => h.id === hazard.id ? {
      ...h,
      ecmDriver: cycle[hazard.ecmDriver]
    } : h);
    onUpdateGeneral(newHazards, generalEquipment);
  };
  const handleAddHazardEquipment = (hazardId: string) => {
    const trimmed = newHazardEquipment.trim();
    if (trimmed) {
      const newHazards = generalHazards.map(h => h.id === hazardId && !h.equipment.includes(trimmed) ? {
        ...h,
        equipment: [...h.equipment, trimmed]
      } : h);
      onUpdateGeneral(newHazards, generalEquipment);
    }
    setNewHazardEquipment('');
    setShowAddHazardEquipment(null);
  };
  const handleRemoveHazardEquipment = (hazardId: string, eqIndex: number) => {
    const newHazards = generalHazards.map(h => h.id === hazardId ? {
      ...h,
      equipment: h.equipment.filter((_, i) => i !== eqIndex)
    } : h);
    onUpdateGeneral(newHazards, generalEquipment);
  };
  const handleAddGeneralEquipment = () => {
    const trimmed = newGeneralEquipment.trim();
    if (trimmed && !generalEquipment.includes(trimmed)) {
      onUpdateGeneral(generalHazards, [...generalEquipment, trimmed]);
    }
    setNewGeneralEquipment('');
    setShowAddGeneralEquipment(false);
  };
  const handleRemoveGeneralEquipment = (index: number) => {
    const newEq = generalEquipment.filter((_, i) => i !== index);
    onUpdateGeneral(generalHazards, newEq);
  };
  const startEdit = (hazardId: string, field: 'eliminateSubstitute' | 'engineering' | 'adminTraining' | 'ppe', currentValue: string) => {
    setEditingField({
      hazardId,
      field
    });
    setEditValue(currentValue);
  };
  const saveEdit = (newText: string) => {
    if (!editingField) return;
    const {
      hazardId,
      field
    } = editingField;
    handleUpdateControl(hazardId, field, newText);
    setEditingField(null);
  };
  const getECMStyle = (driver: ECMDriver) => {
    switch (driver) {
      case 'E':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'C':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'M':
        return 'bg-amber-50 text-amber-600 border-amber-200';
    }
  };
  const controlFields: {
    key: keyof HierarchyControls;
    label: string;
  }[] = [{
    key: 'eliminateSubstitute',
    label: 'Elim/Sub'
  }, {
    key: 'engineering',
    label: 'Engineering'
  }, {
    key: 'adminTraining',
    label: 'Admin/Training'
  }, {
    key: 'ppe',
    label: 'PPE'
  }];
  const getModalTitle = (field: keyof HierarchyControls) => {
    switch (field) {
      case 'eliminateSubstitute':
        return 'Edit Elimination / Substitution';
      case 'engineering':
        return 'Edit Engineering Control';
      case 'adminTraining':
        return 'Edit Admin / Training Control';
      case 'ppe':
        return 'Edit PPE';
    }
  };
  // Only show relevant hazards on the controls screen
  const relevantHazards = generalHazards.filter(h => h.relevant);
  return <div className="flex flex-col h-full" data-id="element-416">
      <div className="mb-3" data-id="element-417">
        <p className="text-sm font-bold text-gray-400 mb-0.5" data-id="element-418">
          General — Controls
        </p>
        <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-419">
          General Site Hazards
        </h2>
        <p className="text-gray-400 text-[11px] mt-0.5" data-id="element-420">
          Review and edit controls for hazards that apply across the full task.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-20 -mx-1 px-1 space-y-3" data-id="element-421">
        {relevantHazards.length === 0 && <div className="text-center py-8" data-id="element-422">
            <p className="text-gray-400 text-sm" data-id="element-423">
              No relevant general hazards.
            </p>
            <p className="text-gray-400 text-xs mt-1" data-id="element-424">
              All general hazards were marked as not relevant in the review.
            </p>
          </div>}
        {relevantHazards.map((hazard, idx) => <motion.div key={hazard.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.05
      }} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-425">
            {/* Header row — read-only */}
            <div className="p-3 pb-2" data-id="element-426">
              <h5 className="font-bold text-sm leading-snug text-gray-900" data-id="element-427">
                {hazard.hazard}
              </h5>
              <p className="text-xs mt-0.5 text-gray-500" data-id="element-428">{hazard.source}</p>
            </div>

            <div className="px-3 pb-3 space-y-2" data-id="element-429">
              <button onClick={() => handleCycleECM(hazard)} className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors ${getECMStyle(hazard.ecmDriver)}`} data-id="element-430">
                {hazard.ecmDriver} — {ECM_LABELS[hazard.ecmDriver]}
              </button>

              {/* Controls — tap to edit via modal */}
              <div className="space-y-1.5 bg-gray-50 rounded-lg p-2.5 border border-gray-200" data-id="element-431">
                {controlFields.map(({
              key,
              label
            }) => <div key={key} data-id="element-432">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-433">
                      {label}
                    </span>
                    <p onClick={() => startEdit(hazard.id, key, hazard.controls[key])} className="text-xs text-gray-700 leading-snug mt-0.5 cursor-pointer" data-id="element-434">
                      {hazard.controls[key]}
                    </p>
                  </div>)}
              </div>

              <div data-id="element-435">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-436">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-437">
                    Tools & Equipment
                  </span>
                  <button onClick={() => setShowAddHazardEquipment(hazard.id)} className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-438">
                    <PlusIcon className="w-2.5 h-2.5" data-id="element-439" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1" data-id="element-440">
                  <AnimatePresence data-id="element-441">
                    {showAddHazardEquipment === hazard.id && <motion.div initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} className="flex items-center gap-1 bg-gray-50 border border-yellow-400 rounded-full pl-2 pr-1 py-0.5" data-id="element-442">
                        <input autoFocus value={newHazardEquipment} onChange={e => setNewHazardEquipment(e.target.value)} onBlur={() => handleAddHazardEquipment(hazard.id)} onKeyDown={e => {
                    if (e.key === 'Enter') handleAddHazardEquipment(hazard.id);
                    if (e.key === 'Escape') {
                      setShowAddHazardEquipment(null);
                      setNewHazardEquipment('');
                    }
                  }} placeholder="Add..." className="w-20 bg-transparent text-gray-900 text-[11px] outline-none" data-id="element-443" />
                      </motion.div>}
                  </AnimatePresence>
                  {hazard.equipment.map((eq, i) => <span key={i} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 pl-2 pr-1 py-0.5 rounded-full text-[11px] text-gray-700" data-id="element-444">
                      {eq}
                      <button onClick={() => handleRemoveHazardEquipment(hazard.id, i)} className="p-0.5 text-gray-400 hover:text-red-500 rounded-full" data-id="element-445">
                        <XIcon className="w-2.5 h-2.5" data-id="element-446" />
                      </button>
                    </span>)}
                  {hazard.equipment.length === 0 && showAddHazardEquipment !== hazard.id && <span className="text-gray-400 text-[11px] italic" data-id="element-447">
                        None
                      </span>}
                </div>
              </div>
            </div>
          </motion.div>)}

        {/* Common Tools & Equipment */}
        <div className="pt-2" data-id="element-448">
          <div className="flex items-center justify-between mb-2" data-id="element-449">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-450">
              Common Tools & Equipment
            </span>
            <button onClick={() => setShowAddGeneralEquipment(true)} className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-451">
              <PlusIcon className="w-3 h-3" data-id="element-452" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-453">
            <AnimatePresence data-id="element-454">
              {showAddGeneralEquipment && <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} className="flex items-center gap-1 bg-gray-50 border border-yellow-400 rounded-full pl-2 pr-1 py-0.5" data-id="element-455">
                  <input autoFocus value={newGeneralEquipment} onChange={e => setNewGeneralEquipment(e.target.value)} onBlur={handleAddGeneralEquipment} onKeyDown={e => {
                if (e.key === 'Enter') handleAddGeneralEquipment();
                if (e.key === 'Escape') {
                  setShowAddGeneralEquipment(false);
                  setNewGeneralEquipment('');
                }
              }} placeholder="Add..." className="w-24 bg-transparent text-gray-900 text-xs outline-none" data-id="element-456" />
                </motion.div>}
            </AnimatePresence>
            {generalEquipment.map((eq, i) => <span key={i} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 pl-2 pr-1 py-1 rounded-full text-xs text-gray-700" data-id="element-457">
                {eq}
                <button onClick={() => handleRemoveGeneralEquipment(i)} className="p-0.5 text-gray-400 hover:text-red-500 rounded-full" data-id="element-458">
                  <XIcon className="w-3 h-3" data-id="element-459" />
                </button>
              </span>)}
            {generalEquipment.length === 0 && !showAddGeneralEquipment && <span className="text-gray-400 text-xs italic" data-id="element-460">None</span>}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 pt-3 pb-0 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent" data-id="element-461">
        <PrimaryButton onClick={onNext} data-id="element-462">Continue</PrimaryButton>
      </div>

      {/* Edit Control Modal */}
      <EditItemModal isOpen={!!editingField} initialText={editValue} title={editingField ? getModalTitle(editingField.field) : 'Edit Control'} onSave={saveEdit} onCancel={() => setEditingField(null)} data-id="element-463" />
    </div>;
}